import type { Report } from '@reports/domain/entities';
import { createReportId } from '@reports/domain/entities';
import {
  ReportTypes,
  ReportInputTypes,
  ReportBlockTypes,
  ReportDataFormats,
  ReportChartTypes,
  ExportFormats,
} from '@reports/domain/types';
import {
  createTableBlockConfig,
  createChartBlockConfig,
  createReportFilterConfig,
  createExportConfig,
} from '@reports/domain/value-objects';

import { REPORT_FILTERS_KEYS } from '../reportKeysConst';
import { REPORT_ENDPOINTS } from '@reports/application/configs/endpoints';
import { REPORT_ACADEMY_LINKS } from '../reportAcademyConst';

export const paymentsReportDefinition: Report = {
  id: createReportId('payments-report'),
  type: ReportTypes.PAYMENTS,
  metadata: {
    name: 'reportPayments:name',
    description: 'reportPayments:description',
    tooltip: 'reportPayments:tooltip',
    parrotAcademyLink: REPORT_ACADEMY_LINKS.PAYMENTS,
    filterDateRangeInfo: 'reportPayments:filters.dateRangeInfo',
  },
  filters: [
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.STORE_PAYMENT_TYPE,
      label: 'reportCommon:filterKeys.storePaymentType.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.storePaymentType.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.BRAND,
      label: 'reportCommon:filterKeys.brand.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.brand.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.USER_PAYMENT,
      label: 'reportCommon:filterKeys.userPayment.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.userPayment.placeholder',
      searchable: true,
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.USER_ORDER,
      label: 'reportCommon:filterKeys.userOrder.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.userOrder.placeholder',
      searchable: true,
    }),
  ],
  sections: [
    {
      key: 'chart',
      title: 'reportCommon:sections.charts.title',
      blocks: [
        createChartBlockConfig({
          key: 'totalCollectedByDay',
          title: 'reportPayments:blocks.charts.totalCollectedByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/12`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'xl',
          seriesConfig: [
            {
              key: 'total_collected_amount',
              label: 'reportPayments:blocks.charts.totalCollectedByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'collectedByPaymentType',
          title: 'reportPayments:blocks.charts.collectedByPaymentType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/13`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'md',
          seriesConfig: [
            {
              key: 'total_collected_amount',
              label: 'reportPayments:blocks.charts.collectedByPaymentType.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'cancelledByPaymentType',
          title: 'reportPayments:blocks.charts.cancelledByPaymentType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/14`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'md',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportPayments:blocks.charts.cancelledByPaymentType.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
      ],
    },
    {
      key: 'summary',
      title: 'reportCommon:sections.summary.title',
      blocks: [
        createTableBlockConfig({
          key: 'paymentsSummaryTable',
          endpoint: REPORT_ENDPOINTS.PAYMENTS.SUMMARY,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'payment_type_name_uuid',
              label: 'reportPayments:blocks.summaryTable.columns.paymentTypeNameUuid.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
              bold: true,
            },
            {
              key: 'total_registered_amount',
              label: 'reportPayments:blocks.summaryTable.columns.totalRegisteredAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_cancelled_amount',
              label: 'reportPayments:blocks.summaryTable.columns.totalCancelledAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_active_amount',
              label: 'reportPayments:blocks.summaryTable.columns.totalActiveAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_tip_amount',
              label: 'reportPayments:blocks.summaryTable.columns.totalTipAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_collected_amount',
              label: 'reportPayments:blocks.summaryTable.columns.totalCollectedAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: false,
        }),
      ],
    },
    {
      key: 'details',
      title: 'reportCommon:sections.details.title',
      blocks: [
        createTableBlockConfig({
          key: 'paymentsDetailsTable',
          endpoint: REPORT_ENDPOINTS.PAYMENTS.DETAILS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'payment_date',
              label: 'reportPayments:blocks.detailsTable.columns.period.label',
              format: {
                type: ReportDataFormats.DATE,
              },
              align: 'left',
            },
            {
              key: 'payment_time',
              label: 'reportPayments:blocks.detailsTable.columns.hour.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'right',
            },
            {
              key: 'payment_status',
              label: 'reportPayments:blocks.detailsTable.columns.status.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'order_reference',
              label: 'reportPayments:blocks.detailsTable.columns.orden.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'charged_by_user',
              label: 'reportPayments:blocks.detailsTable.columns.userPayment.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'account_user',
              label: 'reportPayments:blocks.detailsTable.columns.userOrder.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'payment_type_name',
              label: 'reportPayments:blocks.detailsTable.columns.paymentType.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'total_with_tip',
              label: 'reportPayments:blocks.detailsTable.columns.totalTip.label',
              format: {
                type: ReportDataFormats.CURRENCY,
              },
              align: 'right',
            },
            {
              key: 'tip_amount',
              label: 'reportPayments:blocks.detailsTable.columns.tip.label',
              format: {
                type: ReportDataFormats.CURRENCY,
              },
              align: 'right',
            },
            {
              key: 'total_charged',
              label: 'reportPayments:blocks.detailsTable.columns.totalCollected.label',
              format: {
                type: ReportDataFormats.CURRENCY,
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: true,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
  ],
  export: createExportConfig({
    name: 'reportPayments:export.name',
    endpoint: REPORT_ENDPOINTS.PAYMENTS.EXPORT,
    slugs: ['11', '15'],
    format: ExportFormats.EXCEL,
  }),
};
